import { Drawer, Typography } from 'antd';
import { observer } from 'mobx-react';
import { listShortcuts } from '@/lib/shortcuts';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Title, Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const KeyboardCheatsheet = observer(function KeyboardCheatsheet({ open, onClose }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const shortcuts = listShortcuts();

  const headingColor = isDark ? colors.slate[400] : colors.slate[500];
  const dashedBorder = `1px dashed ${isDark ? colors.slate[700] : colors.slate[200]}`;

  return (
    <Drawer
      title="Keyboard shortcuts"
      open={open}
      onClose={onClose}
      placement="right"
      width={400}
      styles={{ body: { padding: 20 } }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
        Press <Kbd isDark={isDark}>?</Kbd> any time to reopen this panel.
      </Text>

      <Title level={5} style={{ marginTop: 8, marginBottom: 12, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: headingColor }}>
        Navigation
      </Title>
      <ShortcutRow keys={['Cmd', 'K']} description="Open command palette" isDark={isDark} dashedBorder={dashedBorder} />
      <ShortcutRow keys={['Cmd', 'Shift', 'L']} description="Toggle light / dark mode" isDark={isDark} dashedBorder={dashedBorder} />
      <ShortcutRow keys={['?']} description="Show this cheatsheet" isDark={isDark} dashedBorder={dashedBorder} />
      <ShortcutRow keys={['Esc']} description="Close palette / dialogs" isDark={isDark} dashedBorder={dashedBorder} />

      <Title level={5} style={{ marginTop: 24, marginBottom: 12, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: headingColor }}>
        Registered ({shortcuts.length})
      </Title>
      {shortcuts.map((s) => (
        <div key={`${s.meta ? 'M' : ''}${s.shift ? 'S' : ''}${s.key}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: dashedBorder }}>
          <Text style={{ fontSize: 13 }}>{s.description}</Text>
          <span style={{ display: 'flex', gap: 4 }}>
            {s.meta && <Kbd isDark={isDark}>{navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}</Kbd>}
            {s.shift && <Kbd isDark={isDark}>⇧</Kbd>}
            <Kbd isDark={isDark}>{s.key.toUpperCase()}</Kbd>
          </span>
        </div>
      ))}
    </Drawer>
  );
});

function ShortcutRow({ keys, description, isDark, dashedBorder }: { keys: string[]; description: string; isDark: boolean; dashedBorder: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: dashedBorder }}>
      <Text style={{ fontSize: 13 }}>{description}</Text>
      <span style={{ display: 'flex', gap: 4 }}>
        {keys.map((k, i) => (
          <Kbd key={i} isDark={isDark}>{k}</Kbd>
        ))}
      </span>
    </div>
  );
}

function Kbd({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return (
    <kbd
      style={{
        fontSize: 11,
        padding: '2px 6px',
        borderRadius: 4,
        border: `1px solid ${isDark ? colors.slate[600] : colors.slate[200]}`,
        background: isDark ? colors.slate[700] : colors.slate[50],
        color: isDark ? colors.slate[100] : colors.slate[700],
        fontFamily: "'JetBrains Mono', monospace",
        boxShadow: isDark
          ? 'inset 0 -1px 0 rgba(0,0,0,0.4)'
          : 'inset 0 -1px 0 rgba(15,23,42,0.06)',
      }}
    >
      {children}
    </kbd>
  );
}
